import React, { useCallback, useContext, useEffect, useState } from "react";
import modalBorder from "@assets/images/borders/modalBorder.webp";
import contactBorder from "@assets/images/borders/contactBorder.webp";
import useApiError from "@hooks/useApiError";
import useLogout from "@src/hooks/useLogout";
import { CartContext } from "@src/context/CartContext";
import { TotalItemsDispatchContext } from "@src/context/TotalItemsContext";
import { UserDispatchContext } from "@src/context/UserContext";
import type { ILogoutModal } from "@src/types/AppTypes";

const LogoutModal: React.FC<ILogoutModal> = ({ setModal }) => {
  const logout = useLogout();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const setApiError = useApiError();
  const { setCartItems } = useContext(CartContext);
  const totalCartItemsDispatch = useContext(TotalItemsDispatchContext);
  const userDispatch = useContext(UserDispatchContext);

  const confirmLogout = useCallback(async () => {
    try {
      setIsLoading(true);
      await logout();
    } catch (e) {
      setApiError(e);
    } finally {
      setIsLoading(false);
      setModal(false);
    }
  }, [setApiError, logout, setModal]);

  useEffect(() => {
    document.body.style.overflow = "hidden"

    return () => {
      document.body.style.overflow = "unset"
    }
  }, [])

  return (
    <div className="max-w-2xl w-full bg-gradient-to-b from-[#1D1159] to-[#00AEEF] relative rounded-[5px_10px_20px_0px]">
      <img
        src={modalBorder}
        className="absolute top-0 left-0 size-full z-[0]"
        alt="modal border"
      />
      <div
        className={`fixed size-full bg-[#00000080] top-0 left-0 grid place-items-center z-[999]`}
      >
        <div
          className={
            "relative bg-gradient-to-b rounded-[2rem_4rem_4rem_4rem] from-[#1D1159] to-[#00AEEF] max-w-md w-full"
          }
        >
          <div className="relative p-10 max-xl:p-6">
            <img
              className="absolute top-0 left-0 size-full"
              src={contactBorder}
              alt=""
            />
            <div className="relative">
              <div className="flex items-center justify-between mb-4 gap-10">
                <h2 className="text-primary text-xl">Logout Confirmation.</h2>
                <button
                  onClick={() => {
                    setModal(false);
                  }}
                  className="text-primary text-3xl ml-auto duration-150 hover:scale-95"
                  aria-label="Close"
                >
                  <i className="ri-close-circle-line"></i>
                </button>
              </div>
              <p className="text-3xl text-white text-center">Are you sure?</p>
              <div className="mt-5 text-center">
                <button
                  className="flex justify-center text-xl w-full mt-5 p-[13px] rounded-[5px_27px_19px_25px] bg-contain bg-no-repeat bg-center bg-[url('@assets/images/btnBgImage.webp')] duration-150 hover:scale-95 disabled:opacity-70 disabled:hover:cursor-not-allowed"
                  onClick={async () => {
                    setCartItems({
                      type: "updateCart",
                      payload: [],
                    });
                    totalCartItemsDispatch({
                      payload: 0,
                      type: "updateTotalCount",
                    });
                    userDispatch({ type: "setUser", payload: null });
                    localStorage.removeItem("lID");
                    localStorage.removeItem("letype");
                    localStorage.removeItem("lesubtype");
                    await confirmLogout();
                  }}
                  aria-label="Logout"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <i className="ri-loop-right-line btn-loader"></i>
                  ) : (
                    "Log out"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoutModal;
